import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Search, Eye, Mail, Send, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CommunicationHistory } from '../../components/CommunicationHistory';

const API = '/api';

export const AdminTrainingRequestsPage = () => {
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      let url = `${API}/admin/training-requests`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      const response = await fetch(`${API}/admin/training-requests/${requestId}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchRequests();
        if (selectedRequest?.request_id === requestId) {
          setSelectedRequest(prev => ({ ...prev, status }));
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/training-requests/${selectedRequest.request_id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reply_message: replyMessage }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.email_status === 'mocked' ? 'Reply logged (test mode)' : 'Reply sent successfully');
        setReplyDialogOpen(false);
        setReplyMessage('');
        fetchRequests();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filteredRequests = requests.filter(req => 
    req.organization_name.toLowerCase().includes(search.toLowerCase()) ||
    req.contact_person.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      'needs-analysis': 'bg-yellow-100 text-yellow-800',
      'proposal-sent': 'bg-purple-100 text-purple-800',
      confirmed: 'bg-cyan-100 text-cyan-800',
      scheduled: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      'follow-up': 'bg-pink-100 text-pink-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const statuses = ['new', 'needs-analysis', 'proposal-sent', 'confirmed', 'scheduled', 'delivered', 'follow-up', 'closed'];

  return (
    <div className="p-6" data-testid="admin-training-requests-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">{t('admin.trainingRequests')}</h1>
        <p className="text-[#6B7280]">Manage customized training requests</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder={t('admin.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s} value={s}>{s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">{t('admin.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Topics</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                    <TableHead>{t('admin.date')}</TableHead>
                    <TableHead className="text-end">{t('admin.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((req, index) => (
                    <TableRow key={req.request_id} data-testid={`request-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{req.organization_name}</p>
                          <p className="text-sm text-[#6B7280]">{req.contact_person}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {req.topics?.slice(0, 2).map((topic, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{topic}</Badge>
                          ))}
                          {req.topics?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{req.topics.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{req.email}</p>
                          <p className="text-[#6B7280]">{req.country}</p>
                        </div>
                      </TableCell>
                      <TableCell>{req.audience_size || '-'}</TableCell>
                      <TableCell>
                        <Select value={req.status} onValueChange={(v) => updateStatus(req.request_id, v)}>
                          <SelectTrigger className="w-36 h-8">
                            <Badge className={getStatusColor(req.status)}>{req.status.replace('-', ' ')}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map(s => (
                              <SelectItem key={s} value={s}>{s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#6B7280]">
                        {req.created_at ? format(new Date(req.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(req)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Details Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Training Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#6B7280]">Organization</p>
                  <p className="font-medium">{selectedRequest.organization_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Contact Person</p>
                  <p className="font-medium">{selectedRequest.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Email</p>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Phone</p>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Country</p>
                  <p className="font-medium">{selectedRequest.country}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Industry</p>
                  <p className="font-medium">{selectedRequest.industry || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Participants</p>
                  <p className="font-medium">{selectedRequest.audience_size || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Language</p>
                  <p className="font-medium">{selectedRequest.language}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Preferred Dates</p>
                  <p className="font-medium">{selectedRequest.preferred_dates || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Preferred Location</p>
                  <p className="font-medium">{selectedRequest.preferred_location}</p>
                </div>
                {selectedRequest.budget_max && (
                  <div>
                    <p className="text-sm text-[#6B7280]">Budget Range</p>
                    <p className="font-medium">
                      {selectedRequest.currency} {selectedRequest.budget_min?.toLocaleString()} - {selectedRequest.budget_max?.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {selectedRequest.topics?.length > 0 && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Topics</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.topics.map((topic, i) => (
                      <Badge key={i} variant="secondary">{topic}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedRequest.training_goals && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Training Goals</p>
                  <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedRequest.training_goals}</p>
                </div>
              )}

              {selectedRequest.notes && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Notes</p>
                  <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Reply Action */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    Communication
                  </h4>
                </div>
                <Button
                  onClick={() => setReplyDialogOpen(true)}
                  data-testid="reply-training-request-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Reply to Request
                </Button>
              </div>

              {/* Communication History */}
              <CommunicationHistory
                recordType="training_request"
                recordId={selectedRequest.request_id}
                title="Communication History"
                maxHeight="200px"
              />

              <div className="border-t pt-4">
                <p className="text-sm text-[#6B7280] mb-2">Update Status</p>
                <Select value={selectedRequest.status} onValueChange={(v) => updateStatus(selectedRequest.request_id, v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s} value={s}>{s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Reply to Training Request
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>To:</strong> {selectedRequest.contact_person} &lt;{selectedRequest.email}&gt;</p>
                <p><strong>Organization:</strong> {selectedRequest.organization_name}</p>
              </div>
              
              <div>
                <Label htmlFor="replyMessage">Your Reply</Label>
                <Textarea
                  id="replyMessage"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                />
              </div>
              
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                Email is currently in test mode. Messages are logged but not actually sent until email service is activated.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReply} disabled={sending}>
              {sending ? 'Sending...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrainingRequestsPage;
